import { useCallback, useEffect, useState } from 'react';
import { apiCall } from '../../utils/fetchAPI';
import { useAuth } from '../../context/AuthContext';
import { translateApiMessage, translateViolations } from '../../utils/apiMessages';

const EMPTY = {
  username: '',
  email: '',
  citySlug: '',
  neighborhoodUuid: '',
  currentPassword: '',
  newPassword: '',
};

/** The fields the form shows, and so the only ones an error can sit under. */
export const FIELDS = ['username', 'email', 'citySlug', 'neighborhoodUuid', 'currentPassword', 'newPassword'];

/**
 * What the API is asked to change. Empty strings are sent as null: a resident
 * who lives nowhere in particular is not a resident of "".
 */
export function toPayload(form) {
  const blankToNull = (value) => {
    const trimmed = String(value ?? '').trim();

    return '' === trimmed ? null : trimmed;
  };

  return {
    username: String(form.username ?? '').trim(),
    email: String(form.email ?? '').trim(),
    // The city travels only when no neighbourhood does: the API reads the
    // city off the neighbourhood, so sending both invites them to disagree.
    citySlug: form.neighborhoodUuid ? null : blankToNull(form.citySlug),
    neighborhoodUuid: blankToNull(form.neighborhoodUuid),
    currentPassword: blankToNull(form.currentPassword),
    newPassword: blankToNull(form.newPassword),
  };
}

/**
 * What the form refuses before the round trip. The API checks all of it too
 * and stays the authority; this only points at the field.
 */
export function validateAccount(form) {
  const errors = {};

  if (!String(form.username ?? '').trim()) {
    errors.username = "Choisissez un nom d'utilisateur";
  }

  if (!String(form.email ?? '').trim()) {
    errors.email = 'Entrez votre adresse email';
  }

  const newPassword = String(form.newPassword ?? '');

  if (newPassword && newPassword.length < 8) {
    errors.newPassword = 'Au moins 8 caractères';
  }

  return errors;
}

/**
 * The signed-in resident's own account: read it, change it.
 *
 * Where they live is two fields that are really one choice — a neighbourhood
 * belongs to a city — so picking a city loads its neighbourhoods and picking a
 * neighbourhood is what the API is told about.
 */
export function useMyAccount() {
  const { user, refreshUser } = useAuth();

  const [form, setForm] = useState(EMPTY);
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Seeded from the session rather than re-read: the account is already in
  // hand, and a second request would only make the form appear a beat later.
  useEffect(() => {
    if (!user) {
      return;
    }

    setForm({
      ...EMPTY,
      username: user.username || '',
      email: user.email || '',
      citySlug: user.city?.slug || '',
      neighborhoodUuid: user.neighborhood?.uuid || '',
    });
    setLoading(false);
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await apiCall('/cities', { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (!cancelled) {
          setCities(data.data || []);
        }
      } catch {
        // The form still works without the list; the field is optional.
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const loadNeighborhoods = useCallback(async (citySlug) => {
    if (!citySlug) {
      setNeighborhoods([]);
      return;
    }

    try {
      const response = await apiCall(`/cities/${citySlug}/neighborhoods`, { method: 'GET' });
      const data = await response.json().catch(() => ({}));

      setNeighborhoods(data.data || []);
    } catch {
      setNeighborhoods([]);
    }
  }, []);

  useEffect(() => {
    loadNeighborhoods(form.citySlug);
  }, [form.citySlug, loadNeighborhoods]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
      // A neighbourhood of the city they just left is not one of theirs.
      ...('citySlug' === name ? { neighborhoodUuid: '' } : {}),
    }));
  };

  const save = async (e) => {
    e?.preventDefault();
    setError('');
    setNotice('');

    const violations = validateAccount(form);

    if (Object.keys(violations).length > 0) {
      setFieldErrors(violations);
      setError('Corrigez les champs signalés ci-dessous');
      return false;
    }

    setFieldErrors({});
    setSaving(true);

    try {
      const response = await apiCall('/me', { method: 'PUT', body: JSON.stringify(toPayload(form)) });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const translated = translateViolations(data.violations);
        setFieldErrors(translated);

        if (0 === Object.keys(translated).filter((field) => FIELDS.includes(field)).length) {
          setError(translateApiMessage(data.error) || 'Vos informations n’ont pas pu être enregistrées');
        }

        return false;
      }

      // The header shows the handle and the avatar letter, so it has to follow.
      await refreshUser();
      setForm((prev) => ({ ...prev, currentPassword: '', newPassword: '' }));
      setNotice('Vos informations sont enregistrées.');

      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    form,
    cities,
    neighborhoods,
    loading,
    saving,
    error,
    notice,
    fieldErrors,
    handleChange,
    save,
  };
}
